import React, { useState, useEffect } from 'react'
import { getRelativeBasePath } from '../../../global/environments/path.js'
import { lookupFundType } from '../../../components/Resources/Helpers/Utilities.js'

import {
  getCartOrderData,
  deleteCartOrderItem,
  createEmail,
  sendEmail,
  isProduction,
} from '../../../components/Resources/Data/ResourcesDataSource.js'
import Table from '../../../components/Table/Table.jsx'

const fromEmail = 'litordersystem@columbiathreadneedle.com'
const toEmailProduction = 'columbiaserviceinquiries@sscinc.com'
const toEmail = 'columbiaserviceinquiries@sscinc.com'

export const CartOrder = () => {
  const [funds, setFunds] = useState([])
  const [selectedMode, setSelectedMode] = useState('cart')
  const [inputError, setInputError] = useState('')
  const [addressInfo, setAddressInfo] = useState({
    name: '',
    firmname: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    email: '',
    phone: '',
  })

  const getAspect = (section) => {
    const aspects = [
      {
        name: '#cart-order',
        info: {
          selector: [],
          getData: getCartOrderData,
          renderOptions: () => {
            return <>{renderPerformanceOptions()}</>
          },
          columns: [
            {
              name: 'fundName',
              className: 'Table-header',
              ariaLabel: 'Fund Name: activate to sort column descending',
              content: <div>Fund Name</div>,
              button: {
                className: 'Table-headerNode u-padding2gu u-paddingRight3gu',
                sort: 'fundName',
                sortType: 'string',
              },
              cell: {
                content: (fund) => (
                  <div className="u-textBold u-sm-textNone u-textColorBrand">
                    {fund.fundName}
                  </div>
                ),
              },
            },
            {
              name: 'fundType',
              className: 'Table-header',
              ariaLabel: 'Fund Type: activate to sort column descending',
              content: <div>Fund Type</div>,
              button: {
                className: 'Table-headerNode u-padding2gu u-paddingRight3gu',
                sort: 'productType',
                sortType: 'string',
              },
              cell: {
                content: (fund) => (
                  <div className="u-textNoWrap">
                    {lookupFundType(fund.productType)}
                  </div>
                ),
              },
            },
            {
              name: 'literatureType',
              className: 'Table-header',
              ariaLabel: 'Literature Type: activate to sort column descending',
              content: <div>Literature Type</div>,
              button: {
                className: 'Table-headerNode u-padding2gu u-paddingRight3gu',
                sort: 'fileDDocType',
                sortType: 'string',
              },
              cell: {
                content: (fund) => (
                  <div className="u-textNoWrap">{fund.fileDDocType}</div>
                ),
              },
            },
            {
              name: 'quantity',
              className: 'Table-header',
              ariaLabel: 'Quantity',
              content: <div>Quantity</div>,
              cell: {
                content: (fund) => (
                  <>
                    {selectedMode === 'confirmation' ? (
                      <>{fund.quantity}</>
                    ) : (
                      <div className="u-flex u-flexAlignItemsCenter">
                        <div className="Dropdown cart-order-dropdown u-sm-marginVert1gu">
                          <select
                            id={`Dropdown-${fund.cartId}`}
                            className="Dropdown-menu"
                            defaultValue={
                              (fund.quantity && fund.quantity.toString()) || '1'
                            }
                            onChange={onChangeQuantityDropdown}
                          >
                            {[
                              '1',
                              '2',
                              '3',
                              '4',
                              '5',
                              '6',
                              '7',
                              '8',
                              '9',
                              '10',
                            ].map((i) => {
                              return (
                                <option key={i} value={i}>
                                  {i}
                                </option>
                              )
                            })}
                          </select>
                        </div>
                      </div>
                    )}
                  </>
                ),
              },
            },
            {
              name: 'remove',
              className: 'Table-header',
              ariaLabel: 'Remove',
              content: <div>Remove</div>,
              cell: {
                content: (fund) => (
                  <div
                    id={`Remove-${fund.cartId}`}
                    className="u-padding2gu"
                    style={{ cursor: 'pointer' }}
                    onClick={onClickRemoveItem}
                  >
                    <span
                      className="Icon Icon--cti u-textSizeMsrp3"
                      aria-hidden="true"
                    >
                      
                    </span>
                  </div>
                ),
              },
              hide: selectedMode === 'confirmation',
            },
          ],
        },
      },
    ]

    return aspects.find((a) => a.name === section).info
  }

  useEffect(() => {
    initializeFunds()
  }, [])

  const initializeFunds = async () => {
    const aspect = getAspect('#cart-order')
    const data = await aspect.getData(aspect.tag)
    const _funds = aspect.selector.reduce(
      (prev, curr) => (curr.isArray ? prev[curr.name][0] : prev[curr.name]),
      data
    )
    setFunds(_funds)
  }

  const validateEmail = () => {
    const invalidFields = []

    if (!addressInfo.name) {
      invalidFields.push('Full Name')
    }

    if (!addressInfo.address1) {
      invalidFields.push('Address 1')
    }

    if (!addressInfo.city) {
      invalidFields.push('City')
    }

    if (!addressInfo.state) {
      invalidFields.push('State')
    }

    if (!addressInfo.zip) {
      invalidFields.push('Zip Code')
    }

    if (!addressInfo.email) {
      invalidFields.push('Email')
    }

    setInputError(
      invalidFields.length > 0 ? invalidFields.join(', ') + ' is required.' : ''
    )

    return invalidFields.length === 0
  }

  const getAddressBodyText = () => {
    let body = 'Requested by: \n'

    body += addressInfo.name + '\n'
    body += 'Firm name: ' + addressInfo.firmname + '\n'
    body += addressInfo.address1 + '\n'

    if (addressInfo.address2) {
      body += addressInfo.address2 + '\n'
    }

    body += addressInfo.city + ', '
    body += addressInfo.state + ' '
    body += addressInfo.zip + '\n'

    body += 'Phone: ' + addressInfo.phone + '\n'
    body += 'Email: ' + addressInfo.email + '\n\n'

    return body
  }

  const getCartItemsBody = () => {
    let body = ''

    const _funds = funds

    _funds.forEach((fund) => {
      const type = lookupFundType(fund.productType)

      body +=
        type +
        ' - ' +
        fund.fundName +
        ' Class ' +
        fund.fundClass +
        ' - ' +
        fund.fileName +
        ' - Quantity: ' +
        fund.quantity +
        '\n'
    })

    return body
  }

  const composeEmail = async () => {
    let emailBody =
      'The following literature has been requested from Columbia Threadneedle Investments.\n\n'

    emailBody += getAddressBodyText()
    emailBody += getCartItemsBody()

    emailBody +=
      '\nYou can expect to receive your order 7 - 10 days after the order ships.\n\n'
    emailBody +=
      'If you have any questions or need anything further please call a Client Services Representative at 1.800.345.6611 between the hours of 8 a.m. - 7 p.m. ET. This email address cannot accept replies.\n'

    const to = !isProduction() ? [toEmail] : [toEmailProduction]
    const subject = 'Online Literature Order'
    const emailPayload = createEmail(fromEmail, to, subject, emailBody, [])
    const sendEmailResponse = await sendEmail(emailPayload)

    if (sendEmailResponse.ok) {
      funds.forEach((fund) => deleteCartOrderItem(fund))

      return 1
    } else {
      return 0
    }
  }

  const onChangeQuantityDropdown = (event) => {
    const _funds = funds

    const _fund = _funds.find(
      (fund) => fund.cartId === event.currentTarget.id.split('-')[1]
    )

    _fund.quantity = parseInt(event.currentTarget.value)

    setFunds(_funds)
  }

  const onClickRemoveItem = (event) => {
    const _funds = funds

    const _fund = _funds.find(
      (fund) => fund.cartId === event.currentTarget.id.split('-')[1]
    )

    deleteCartOrderItem(_fund)

    initializeFunds()
  }

  const onChangeInput = (event) => {
    const _addressInfo = { ...addressInfo }

    _addressInfo[event.currentTarget.id] = event.currentTarget.value

    setAddressInfo(_addressInfo)
  }

  const onClickContinueBrowsing = (event) => {
    event.preventDefault()

    window.location = getRelativeBasePath() + '/investment-products'
  }

  const onClickCheckout = (event) => {
    event.preventDefault()

    setSelectedMode('checkout')

    window.scrollTo(0, 0)
  }

  const onClickBackToCart = (event) => {
    event.preventDefault()

    setSelectedMode('cart')
  }

  const onClickSubmitOrder = async (event) => {
    event.preventDefault()

    if (!validateEmail()) {
      return
    }

    if (await composeEmail()) {
      setSelectedMode('confirmation')
    } else {
      setInputError(
        'There was an error submitting this order. Please try again later.'
      )
    }
  }

  const renderPerformanceOptions = () => {
    return (
      <>
        {selectedMode === 'confirmation' && (
          <div>
            <p>
              Thank you for your order and for your interest in Columbia
              Threadneedle Investments.
            </p>
            <p>
              You will receive a confirmation of your order via email within 2
              business days. You can expect to receive your order 7-10 days
              after the order ships.
            </p>
            <p>
              If you have any questions or need anything further please call a
              Client Services Representative at 1.800.345.6611 between the hours
              of 8:00 a.m. - 7:00 p.m. Eastern time.
            </p>
            <h2 className="u-marginTop6gu">Your Order</h2>
          </div>
        )}
        {selectedMode === 'cart' && <h2>Mail Order Summary</h2>}
      </>
    )
  }

  const renderButtons = () => {
    return (
      <div className="u-marginTop15gu">
        <button
          className="Button Button--primary Button--reversed u-marginRight4gu u-paddingHoriz5gu u-sm-sizeFull"
          onClick={
            selectedMode === 'cart'
              ? onClickContinueBrowsing
              : onClickBackToCart
          }
        >
          {selectedMode === 'cart' ? 'Continue Browsing' : 'Back to Cart'}
        </button>
        <button
          className="Button Button--primary u-paddingHoriz10gu u-sm-marginTop2gu u-sm-sizeFull"
          onClick={
            selectedMode === 'cart' ? onClickCheckout : onClickSubmitOrder
          }
          disabled={!funds || funds.length < 1}
        >
          {selectedMode === 'cart' ? 'Check Out' : 'Submit Order'}
        </button>
      </div>
    )
  }

  const aspect = getAspect('#cart-order')

  const pageTitle = () => {
    const title =
      selectedMode === 'cart'
        ? 'My Cart'
        : selectedMode === 'checkout'
          ? 'Checkout'
          : 'Order Confirmation'

    const ele = document?.getElementById('mycart')
    const heading = ele?.getElementsByTagName('h1')
    if (heading) {
      heading[0].innerHTML = title
    }
  }

  useEffect(() => {
    pageTitle()
  }, [selectedMode])

  return (
    <div className="card-order-scope investment-products">
      <div className="u-paddingVert2gu u-bgColorLight">
        <div className="u-sizeConstrained Card Content u-paddingTop5gu u-marginBottom10gu">
          {selectedMode === 'checkout' && (
            <div className="u-size1of2 u-sm-sizeFull">
              <div className="u-flex u-flexJustifyBetween u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0">
                <p className="u-textBold">Mailing Address</p>
                <p className="u-textItalic">
                  <span className="u-textColorAlert">*</span> Required
                  Information
                </p>
              </div>
              <form
                method="post"
                role="form"
                action="#"
                id="cart-checkout-form"
                name="cart-checkout-form"
                className="form-horizontal cart-checkout-form clearfix"
                noValidate="novalidate"
              >
                <div className="form-group u-flex u-flexJustifyBetween u-marginVert3gu">
                  <label
                    htmlFor="name"
                    className="u-sm-flexNone u-sm-marginRight1gu"
                  >
                    Full Name{' '}
                    <span className="u-textColorAlert" aria-required="true">
                      *
                    </span>
                  </label>
                  <div className="u-size3of4">
                    <input
                      onChange={onChangeInput}
                      type="text"
                      aria-required="true"
                      className="u-sizeFull"
                      id="name"
                      name="name"
                      maxLength="255"
                      autoComplete="on"
                      spellCheck="false"
                      placeholder="First and Last Name"
                    />
                  </div>
                </div>
                <div className="form-group u-flex u-flexJustifyBetween u-marginVert3gu">
                  <label htmlFor="firmname">Firm Name</label>
                  <div className="u-size3of4">
                    <input
                      onChange={onChangeInput}
                      type="text"
                      aria-required="true"
                      className="u-sizeFull"
                      id="firmname"
                      name="firmname"
                      maxLength="255"
                      placeholder="Firm Name"
                    />
                  </div>
                </div>
                <div className="form-group u-flex u-flexJustifyBetween u-marginVert3gu">
                  <label
                    htmlFor="address1"
                    className="u-sm-flexNone u-sm-marginRight1gu"
                  >
                    Mailing Address{' '}
                    <span className="u-textColorAlert" aria-required="true">
                      *
                    </span>
                  </label>
                  <div className="u-size3of4">
                    <input
                      onChange={onChangeInput}
                      type="text"
                      aria-required="true"
                      className="u-sizeFull"
                      id="address1"
                      name="address1"
                      maxLength="255"
                      placeholder="Street Address"
                    />
                  </div>
                </div>
                <div className="form-group u-flex u-flexJustifyBetween u-marginVert3gu">
                  <label htmlFor="address2"></label>
                  <div className="col-sm-push-2 u-size3of4">
                    <input
                      onChange={onChangeInput}
                      type="text"
                      aria-required="true"
                      className="u-sizeFull"
                      id="address2"
                      name="address2"
                      maxLength="255"
                      placeholder="Street Address 2 (optional)"
                    />
                  </div>
                </div>
                <div className="form-group u-flex u-flexJustifyBetween u-marginVert3gu">
                  <label htmlFor="city"></label>
                  <div className="col-sm-push-2 u-size3of4">
                    <input
                      onChange={onChangeInput}
                      type="text"
                      aria-required="true"
                      className="u-sizeFull"
                      id="city"
                      name="city"
                      maxLength="255"
                      autoComplete="on"
                      spellCheck="false"
                      placeholder="City"
                    />
                  </div>
                </div>
                <div className="form-group u-flex u-flexJustifyBetween u-marginVert3gu">
                  <label htmlFor="state"></label>
                  <div className="u-flex u-size3of4 u-flexJustifyBetween">
                    <div className="u-size1of2">
                      <div className="cart-checkout-select-container">
                        <select
                          onChange={onChangeInput}
                          className="u-sizeFull state-select cti-select"
                          style={{ height: 24 }}
                          id="state"
                          name="state"
                        >
                          <option value="">Select a State</option>
                          <option value="AL">AL</option>
                          <option value="AK">AK</option>
                          <option value="AZ">AZ</option>
                          <option value="AR">AR</option>
                          <option value="CA">CA</option>
                          <option value="CO">CO</option>
                          <option value="CT">CT</option>
                          <option value="DE">DE</option>
                          <option value="DC">DC</option>
                          <option value="FL">FL</option>
                          <option value="GA">GA</option>
                          <option value="HI">HI</option>
                          <option value="ID">ID</option>
                          <option value="IL">IL</option>
                          <option value="IN">IN</option>
                          <option value="IA">IA</option>
                          <option value="KS">KS</option>
                          <option value="KY">KY</option>
                          <option value="LA">LA</option>
                          <option value="ME">ME</option>
                          <option value="MD">MD</option>
                          <option value="MA">MA</option>
                          <option value="MI">MI</option>
                          <option value="MN">MN</option>
                          <option value="MS">MS</option>
                          <option value="MO">MO</option>
                          <option value="MT">MT</option>
                          <option value="NE">NE</option>
                          <option value="NV">NV</option>
                          <option value="NH">NH</option>
                          <option value="NJ">NJ</option>
                          <option value="NM">NM</option>
                          <option value="NY">NY</option>
                          <option value="NC">NC</option>
                          <option value="ND">ND</option>
                          <option value="OH">OH</option>
                          <option value="OK">OK</option>
                          <option value="OR">OR</option>
                          <option value="PA">PA</option>
                          <option value="RI">RI</option>
                          <option value="SC">SC</option>
                          <option value="SD">SD</option>
                          <option value="TN">TN</option>
                          <option value="TX">TX</option>
                          <option value="UT">UT</option>
                          <option value="VT">VT</option>
                          <option value="VA">VA</option>
                          <option value="WA">WA</option>
                          <option value="WV">WV</option>
                          <option value="WI">WI</option>
                          <option value="WY">WY</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <input
                        onChange={onChangeInput}
                        type="text"
                        aria-required="true"
                        className="u-sizeFull"
                        id="zip"
                        name="zip"
                        maxLength="10"
                        autoComplete="on"
                        spellCheck="false"
                        placeholder="ZIP Code"
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group u-flex u-flexJustifyBetween u-marginVert3gu">
                  <label htmlFor="phone">Phone</label>
                  <div className="u-size3of4">
                    <input
                      onChange={onChangeInput}
                      type="tel"
                      aria-required="true"
                      className="u-sizeFull"
                      id="phone"
                      name="phone"
                      maxLength="50"
                      autoComplete="on"
                      spellCheck="false"
                      placeholder="Phone Number"
                    />
                  </div>
                </div>
                <div className="form-group u-flex u-flexJustifyBetween u-marginVert3gu">
                  <label
                    htmlFor="email"
                    className="u-sm-flexNone u-sm-marginRight1gu"
                  >
                    Email Address{' '}
                    <span className="u-textColorAlert" aria-required="true">
                      *
                    </span>
                  </label>
                  <div className="u-size3of4">
                    <input
                      onChange={onChangeInput}
                      type="email"
                      aria-required="true"
                      className="u-sizeFull"
                      id="email"
                      name="email"
                      maxLength="255"
                      autoComplete="on"
                      spellCheck="false"
                      placeholder="email@email.com"
                    />
                  </div>
                </div>
                <div className="form-group u-flex u-flexJustifyBetween u-marginVert3gu">
                  <label htmlFor="inputError"></label>
                  <div className="u-size3of4">
                    {inputError && (
                      <div className="u-textColorAlert">{inputError}</div>
                    )}
                  </div>
                </div>
              </form>
            </div>
          )}
          {(selectedMode === 'cart' || selectedMode === 'confirmation') && (
            <>
              <div className="u-flex u-flexAlignItemsCenter u-flexJustifyBetween u-marginVert4gu">
                {aspect.renderOptions()}
              </div>
              <Table
                columns={aspect.columns}
                columnSummaries={aspect.columnSummaries}
                rows={funds}
                emptyMessage="Your cart is currently empty."
              />
            </>
          )}
          {(selectedMode === 'cart' || selectedMode === 'checkout') && (
            <div className="u-flex u-flexAlignItemsCenter u-flexJustifyEnd u-marginVert4gu">
              {renderButtons()}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
